import { html, render, nothing, svg, type TemplateResult } from 'lit-html';
import panelStyles from './pk-report-histogram.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

/**
 * `<pk-report-histogram>` — density bar histogram over time. v0.7
 *
 * THE entry panel of every Reports pack (Reports-Packs-Spec.md § 3.1).
 * Renders stacked bars per time bucket (mode="bars", default) or one
 * colored dot per event (mode="dots", the KB pack variant per § 6.5).
 *
 * Accepts `<pk-report-histogram-bucket>` children, each containing one
 * or more `<pk-report-histogram-segment>` children.
 *
 * Attributes:
 *   `mode`               — `bars` (default) | `dots`
 *   `time-range-from`    — ISO instant, required (x-axis left edge)
 *   `time-range-to`      — ISO instant, required (x-axis right edge)
 *   `bucket-granularity` — `minute` | `hour` | `day` | `week` | `month`
 *   `color-scheme`       — `severity` (default) | `pack`
 *   `height`             — chart height in px (default 120)
 *   `interactive`        — boolean, default true; set "false" for print views
 *   `title`              — optional panel heading
 *
 * Events:
 *   `pk-bucket-click`  — `detail: { bucketStart, bucketEnd, count, breakdown: { [category]: number } }`
 *   `pk-range-select`  — `detail: { from: string, to: string }` (drag-to-zoom)
 *
 * Keyboard (when interactive):
 *   Arrow keys navigate between buckets; Enter / Space fires pk-bucket-click.
 *   Shift+ArrowLeft/Right extends the selection range for drag-to-zoom.
 *
 * ```html
 * <pk-report-histogram
 *   mode="bars"
 *   time-range-from="2026-05-17T00:00:00Z"
 *   time-range-to="2026-05-24T00:00:00Z"
 *   bucket-granularity="day"
 *   height="120">
 *   <pk-report-histogram-bucket
 *     bucket-start="2026-05-17T00:00:00Z"
 *     bucket-end="2026-05-18T00:00:00Z">
 *     <pk-report-histogram-segment category="ai-usage" count="42" color="#0e7c86"></pk-report-histogram-segment>
 *   </pk-report-histogram-bucket>
 * </pk-report-histogram>
 * ```
 */
export class PkReportHistogram extends HTMLElement {
    static readonly tagName = 'pk-report-histogram';

    static get observedAttributes(): string[] {
        return ['mode', 'time-range-from', 'time-range-to', 'bucket-granularity',
                'color-scheme', 'height', 'interactive', 'title'];
    }

    private readonly _root: ShadowRoot;
    private readonly _mutationObserver: MutationObserver;

    // Compat data backing fields
    private _bucketsData:  PkHistogramPoint[] | null = null;
    private _segmentsData: PkHistogramSeries[] | null = null;

    // Drag state
    private _dragStartX = -1;
    private _dragCurrentX = -1;
    private _isDragging = false;

    // Keyboard selection state
    private _focusedBucketIdx = -1;
    private _shiftAnchorIdx = -1;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._mutationObserver = new MutationObserver(() => this._doRender());
    }

    connectedCallback(): void {
        this._upgradeProperty('buckets');
        this._upgradeProperty('segments');
        this._mutationObserver.observe(this, { childList: true, subtree: true, attributes: true });
        this._doRender();
    }

    // ── Compat data API ────────────────────────────────────────────────────────

    /**
     * Imperative data setter (v0.8 compat) — single-series histogram.
     * Builds `<pk-report-histogram-bucket>` children each with one
     * `<pk-report-histogram-segment category="count">`.
     */
    set buckets(value: PkHistogramPoint[] | null | undefined) {
        this._bucketsData = Array.isArray(value) ? value : null;
        this._rebuildBuckets();
    }

    get buckets(): PkHistogramPoint[] | null {
        return this._bucketsData;
    }

    /**
     * Imperative data setter (v0.8 compat) — multi-category histogram.
     * Transposes a list of series into stacked `<pk-report-histogram-bucket>`
     * children, one segment per series per time bucket.
     */
    set segments(value: PkHistogramSeries[] | null | undefined) {
        this._segmentsData = Array.isArray(value) ? value : null;
        this._rebuildBuckets();
    }

    get segments(): PkHistogramSeries[] | null {
        return this._segmentsData;
    }

    private _rebuildBuckets(): void {
        this.querySelectorAll('pk-report-histogram-bucket').forEach(el => el.remove());

        if (this._segmentsData && this._segmentsData.length > 0) {
            this._buildFromSegments(this._segmentsData);
        } else if (this._bucketsData && this._bucketsData.length > 0) {
            this._buildFromBuckets(this._bucketsData);
        }
        // MutationObserver fires _doRender on appended children.
    }

    private _buildFromBuckets(points: PkHistogramPoint[]): void {
        const pts = points
            .map(o => ({ t: histT(o), v: histV(o) }))
            .filter(p => p.t)
            .sort((a, b) => a.t.localeCompare(b.t));

        pts.forEach((p, i) => {
            const bucket = document.createElement('pk-report-histogram-bucket');
            bucket.setAttribute('bucket-start', p.t);
            bucket.setAttribute('bucket-end', pts[i + 1]?.t ?? p.t);
            const seg = document.createElement('pk-report-histogram-segment');
            seg.setAttribute('category', 'count');
            seg.setAttribute('count', String(p.v));
            seg.setAttribute('color', 'var(--pk-color-info, #1a73e8)');
            bucket.appendChild(seg);
            this.appendChild(bucket);
        });
    }

    private _buildFromSegments(series: PkHistogramSeries[]): void {
        // Build a sorted union of all timestamps across all series.
        const tSet = new Set<string>();
        const seriesPts = series.map(s => {
            const map = new Map<string, number>();
            (s.buckets ?? []).forEach(o => {
                const t = histT(o);
                if (t) { tSet.add(t); map.set(t, histV(o)); }
            });
            return { label: s.label ?? '', color: s.color ?? 'var(--pk-color-info, #1a73e8)', map };
        });
        const tList = Array.from(tSet).sort((a, b) => a.localeCompare(b));

        tList.forEach((t, i) => {
            const bucket = document.createElement('pk-report-histogram-bucket');
            bucket.setAttribute('bucket-start', t);
            bucket.setAttribute('bucket-end', tList[i + 1] ?? t);
            seriesPts.forEach(sp => {
                if (!sp.map.has(t)) return;
                const v = sp.map.get(t)!;
                const seg = document.createElement('pk-report-histogram-segment');
                seg.setAttribute('category', sp.label);
                seg.setAttribute('count', String(v));
                seg.setAttribute('color', sp.color);
                bucket.appendChild(seg);
            });
            this.appendChild(bucket);
        });
    }

    private _upgradeProperty(prop: string): void {
        if (Object.prototype.hasOwnProperty.call(this, prop)) {
            const v: unknown = Reflect.get(this, prop);
            Reflect.deleteProperty(this, prop);
            Reflect.set(this, prop, v);
        }
    }

    disconnectedCallback(): void {
        this._mutationObserver.disconnect();
    }

    attributeChangedCallback(): void {
        this._doRender();
    }

    // ── Attribute helpers ─────────────────────────────────────────────────────

    private _mode(): 'bars' | 'dots' {
        return this.getAttribute('mode') === 'dots' ? 'dots' : 'bars';
    }

    private _height(): number {
        const v = parseInt(this.getAttribute('height') ?? '120', 10);
        return isNaN(v) || v <= 0 ? 120 : v;
    }

    private _isInteractive(): boolean {
        const v = this.getAttribute('interactive');
        return v === null || v === '' || v === 'true';
    }

    // ── Data collection ──────────────────────────────────────────────────────

    private _buckets(): BucketData[] {
        const bucketEls = Array.from(this.querySelectorAll('pk-report-histogram-bucket'));
        return bucketEls.map(el => {
            const bucketStart = el.getAttribute('bucket-start') ?? '';
            const bucketEnd   = el.getAttribute('bucket-end') ?? '';
            const segmentEls  = Array.from(el.querySelectorAll('pk-report-histogram-segment'));
            const segments: SegmentData[] = segmentEls.map(seg => ({
                category: seg.getAttribute('category') ?? '',
                count:    parseInt(seg.getAttribute('count') ?? '0', 10) || 0,
                color:    seg.getAttribute('color') ?? 'var(--pk-color-info, #1a73e8)',
            }));
            const totalCount = segments.reduce((s, seg) => s + seg.count, 0);
            const breakdown: Record<string, number> = {};
            segments.forEach(seg => { breakdown[seg.category] = seg.count; });
            return { bucketStart, bucketEnd, segments, totalCount, breakdown };
        });
    }

    // ── Event dispatch ────────────────────────────────────────────────────────

    private _fireBucketClick(bucket: BucketData): void {
        if (!this._isInteractive()) return;
        this.dispatchEvent(new CustomEvent('pk-bucket-click', {
            bubbles:  true,
            composed: true,
            detail: {
                bucketStart: bucket.bucketStart,
                bucketEnd:   bucket.bucketEnd,
                count:       bucket.totalCount,
                breakdown:   bucket.breakdown,
            },
        }));
    }

    private _fireRangeSelect(fromIso: string, toIso: string): void {
        if (!this._isInteractive() || !fromIso || !toIso) return;
        this.dispatchEvent(new CustomEvent('pk-range-select', {
            bubbles:  true,
            composed: true,
            detail: { from: fromIso, to: toIso },
        }));
    }

    // ── SVG layout constants ──────────────────────────────────────────────────

    private _layout(buckets: BucketData[], height: number): LayoutParams {
        const n = buckets.length;
        const AXIS_HEIGHT = 20;
        const PADDING_H   = 8;
        const chartH      = height - AXIS_HEIGHT;
        const totalW      = 600; // SVG viewBox width; scales via preserveAspectRatio
        const usableW     = totalW - PADDING_H * 2;
        const bucketW     = n > 0 ? usableW / n : usableW;
        const BAR_GAP     = Math.max(1, bucketW * 0.12);
        const barW        = Math.max(2, bucketW - BAR_GAP);
        const maxCount    = Math.max(1, ...buckets.map(b => b.totalCount));
        return { n, AXIS_HEIGHT, PADDING_H, chartH, totalW, usableW, bucketW, BAR_GAP, barW, maxCount };
    }

    // ── Bars rendering ────────────────────────────────────────────────────────

    private _renderBars(buckets: BucketData[], height: number): TemplateResult {
        const { AXIS_HEIGHT, PADDING_H, chartH, totalW, bucketW, barW, maxCount } = this._layout(buckets, height);
        const svgH = height;
        const interactive = this._isInteractive();
        const focused = this._focusedBucketIdx;

        const bars = buckets.map((bucket, i) => {
            const x      = PADDING_H + i * bucketW;
            const cx     = x + bucketW / 2;
            const barX   = cx - barW / 2;
            let cumY     = chartH; // draw from bottom up

            // Stack segments bottom-to-top
            const segRects = bucket.segments.map(seg => {
                const segH = (seg.count / maxCount) * chartH * 0.92; // leave small top margin
                cumY -= segH;
                const rect = { segH, segY: cumY, color: seg.color, category: seg.category, count: seg.count };
                return rect;
            });

            const isFocused = focused === i;
            const axisLabel = this._bucketLabel(bucket);

            return svg`
                <g class="bar-bucket"
                   tabindex=${interactive ? '0' : '-1'}
                   role="button"
                   aria-label="Bucket ${axisLabel}, total: ${bucket.totalCount}"
                   data-bucket-idx="${i}"
                   @click=${() => interactive && this._fireBucketClick(bucket)}
                   @keydown=${(e: KeyboardEvent) => this._handleBucketKeydown(e, i, buckets)}>
                    ${segRects.map(r => svg`
                        <rect class="bar-segment"
                              x=${barX} y=${r.segY}
                              width=${barW} height=${Math.max(0, r.segH)}
                              fill=${r.color}
                              rx="1">
                            <title>${r.category}: ${r.count}</title>
                        </rect>
                    `)}
                    ${isFocused ? svg`
                        <rect class="bar-focus-ring"
                              x=${barX - 2} y=${0}
                              width=${barW + 4} height=${chartH}
                              fill="none" stroke="var(--pk-color-primary,#0e7c7b)" stroke-width="2" rx="2">
                        </rect>
                    ` : nothing}
                    <text class="x-axis-label"
                          x=${cx}
                          y=${chartH + AXIS_HEIGHT - 4}>
                        ${axisLabel}
                    </text>
                </g>
            `;
        });

        // Drag select overlay
        const dragOverlay = this._isDragging && this._dragStartX >= 0 && this._dragCurrentX >= 0
            ? svg`
                <rect class="drag-select-overlay"
                      x=${Math.min(this._dragStartX, this._dragCurrentX)}
                      y=${0}
                      width=${Math.abs(this._dragCurrentX - this._dragStartX)}
                      height=${chartH}>
                </rect>
              `
            : nothing;

        return html`
            <svg class="histogram-svg"
                 viewBox="0 0 ${totalW} ${svgH}"
                 preserveAspectRatio="none"
                 style="height: ${svgH}px;"
                 role="img"
                 aria-label="Event density histogram"
                 @mousedown=${(e: MouseEvent) => this._onDragStart(e, buckets)}
                 @mousemove=${(e: MouseEvent) => this._onDragMove(e)}
                 @mouseup=${(e: MouseEvent) => this._onDragEnd(e, buckets)}
                 @mouseleave=${() => this._onDragCancel()}>
                <line class="axis-line" x1="0" y1="${chartH}" x2="${totalW}" y2="${chartH}"></line>
                ${bars}
                ${dragOverlay}
            </svg>
        `;
    }

    // ── Dots rendering ────────────────────────────────────────────────────────

    private _renderDots(buckets: BucketData[], height: number): TemplateResult {
        const { AXIS_HEIGHT, PADDING_H, chartH, totalW, bucketW } = this._layout(buckets, height);
        const svgH       = height;
        const interactive = this._isInteractive();
        const DOT_R      = 5;
        const DOT_GAP    = 2;
        const DOT_STEP   = DOT_R * 2 + DOT_GAP;
        const MAX_DOTS   = 20;

        const columns = buckets.map((bucket, i) => {
            const cx = PADDING_H + i * bucketW + bucketW / 2;
            const axisLabel = this._bucketLabel(bucket);

            // Flatten all segment dots into a list of { color }
            const allDots: string[] = [];
            bucket.segments.forEach(seg => {
                for (let d = 0; d < seg.count; d++) allDots.push(seg.color);
            });

            const visible   = allDots.slice(0, MAX_DOTS);
            const overflow  = Math.max(0, allDots.length - MAX_DOTS);
            const totalDotH = visible.length * DOT_STEP;
            const startY    = chartH - totalDotH;

            const dots = visible.map((color, di) => {
                const cy = startY + di * DOT_STEP + DOT_R;
                return svg`
                    <circle class="dot-circle"
                            cx=${cx} cy=${cy} r=${DOT_R}
                            fill=${color}
                            @click=${() => interactive && this._fireBucketClick(bucket)}>
                        <title>${color}</title>
                    </circle>
                `;
            });

            const pill = overflow > 0 ? (() => {
                const pillW = 28, pillH = 14;
                const pillX = cx - pillW / 2;
                const pillY = startY - pillH - 2;
                return svg`
                    <rect class="overflow-pill-bg" x=${pillX} y=${pillY} width=${pillW} height=${pillH} rx="4" ry="4"></rect>
                    <text class="overflow-pill-text" x=${cx} y=${pillY + pillH / 2}>+${overflow}</text>
                `;
            })() : nothing;

            return svg`
                <g class="bar-bucket"
                   tabindex=${interactive ? '0' : '-1'}
                   role="button"
                   aria-label="Bucket ${axisLabel}, total: ${bucket.totalCount}"
                   data-bucket-idx="${i}"
                   @click=${() => interactive && this._fireBucketClick(bucket)}
                   @keydown=${(e: KeyboardEvent) => this._handleBucketKeydown(e, i, buckets)}>
                    ${dots}
                    ${pill}
                    <text class="x-axis-label"
                          x=${cx}
                          y=${chartH + AXIS_HEIGHT - 4}>
                        ${axisLabel}
                    </text>
                </g>
            `;
        });

        return html`
            <svg class="histogram-svg"
                 viewBox="0 0 ${totalW} ${svgH}"
                 preserveAspectRatio="none"
                 style="height: ${svgH}px;"
                 role="img"
                 aria-label="Event dot density histogram"
                 @mousedown=${(e: MouseEvent) => this._onDragStart(e, buckets)}
                 @mousemove=${(e: MouseEvent) => this._onDragMove(e)}
                 @mouseup=${(e: MouseEvent) => this._onDragEnd(e, buckets)}
                 @mouseleave=${() => this._onDragCancel()}>
                <line class="axis-line" x1="0" y1="${chartH}" x2="${totalW}" y2="${chartH}"></line>
                ${columns}
            </svg>
        `;
    }

    // ── Bucket label ──────────────────────────────────────────────────────────

    private _bucketLabel(bucket: BucketData): string {
        if (!bucket.bucketStart) return '';
        try {
            const d = new Date(bucket.bucketStart);
            const gran = this.getAttribute('bucket-granularity') ?? 'day';
            if (gran === 'minute' || gran === 'hour') {
                return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (gran === 'month') {
                return d.toLocaleDateString([], { month: 'short', year: '2-digit' });
            }
            if (gran === 'week') {
                return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            }
            // day (default)
            return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
        } catch {
            return '';
        }
    }

    // ── Drag-to-zoom ──────────────────────────────────────────────────────────

    private _svgX(e: MouseEvent): number {
        const svgEl = this._root.querySelector<SVGSVGElement>('.histogram-svg');
        if (!svgEl) return 0;
        const rect = svgEl.getBoundingClientRect();
        const svgW = svgEl.viewBox.baseVal.width || 600;
        return ((e.clientX - rect.left) / rect.width) * svgW;
    }

    private _onDragStart(e: MouseEvent, _buckets: BucketData[]): void {
        if (!this._isInteractive()) return;
        this._isDragging  = true;
        this._dragStartX  = this._svgX(e);
        this._dragCurrentX = this._dragStartX;
        this._doRender();
    }

    private _onDragMove(e: MouseEvent): void {
        if (!this._isDragging) return;
        this._dragCurrentX = this._svgX(e);
        this._doRender();
    }

    private _onDragEnd(e: MouseEvent, buckets: BucketData[]): void {
        if (!this._isDragging) return;
        this._isDragging  = false;
        const startX = Math.min(this._dragStartX, this._svgX(e));
        const endX   = Math.max(this._dragStartX, this._svgX(e));
        this._dragStartX   = -1;
        this._dragCurrentX = -1;

        // Only fire if meaningful drag (>5 px in SVG coords)
        if (endX - startX < 5) {
            this._doRender();
            return;
        }

        const layout = this._layout(buckets, this._height());
        const fromIdx = this._xToBucketIdx(startX, layout);
        const toIdx   = this._xToBucketIdx(endX, layout);

        const fromBucket = buckets[Math.max(0, fromIdx)];
        const toBucket   = buckets[Math.min(buckets.length - 1, toIdx)];

        if (fromBucket && toBucket) {
            this._fireRangeSelect(fromBucket.bucketStart, toBucket.bucketEnd);
        }
        this._doRender();
    }

    private _onDragCancel(): void {
        if (!this._isDragging) return;
        this._isDragging   = false;
        this._dragStartX   = -1;
        this._dragCurrentX = -1;
        this._doRender();
    }

    private _xToBucketIdx(svgX: number, layout: LayoutParams): number {
        const relX = svgX - layout.PADDING_H;
        return Math.floor(relX / layout.bucketW);
    }

    // ── Keyboard navigation ───────────────────────────────────────────────────

    private _handleBucketKeydown(e: KeyboardEvent, idx: number, buckets: BucketData[]): void {
        const n = buckets.length;
        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowRight': {
                e.preventDefault();
                const dir  = e.key === 'ArrowLeft' ? -1 : 1;
                const next = Math.max(0, Math.min(n - 1, idx + dir));
                this._focusedBucketIdx = next;

                if (e.shiftKey) {
                    // Range selection mode: anchor at first Shift+Arrow, extend
                    if (this._shiftAnchorIdx < 0) this._shiftAnchorIdx = idx;
                    const minIdx = Math.min(this._shiftAnchorIdx, next);
                    const maxIdx = Math.max(this._shiftAnchorIdx, next);
                    const fromB  = buckets[minIdx];
                    const toB    = buckets[maxIdx];
                    if (fromB && toB) this._fireRangeSelect(fromB.bucketStart, toB.bucketEnd);
                } else {
                    this._shiftAnchorIdx = -1;
                }

                this._doRender();
                // Refocus the newly active bucket element
                requestAnimationFrame(() => {
                    const el = this._root.querySelector<SVGElement>(`[data-bucket-idx="${next}"]`);
                    el?.focus();
                });
                break;
            }
            case 'Enter':
            case ' ': {
                e.preventDefault();
                this._shiftAnchorIdx = -1;
                const bucket = buckets[idx];
                if (bucket) this._fireBucketClick(bucket);
                break;
            }
            case 'Escape':
                this._focusedBucketIdx = -1;
                this._shiftAnchorIdx   = -1;
                this._doRender();
                break;
        }
    }

    // ── Template ──────────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const title   = this.getAttribute('title');
        const height  = this._height();
        const mode    = this._mode();
        const buckets = this._buckets();

        const chart = buckets.length === 0
            ? html`
                <div class="empty-state-wrapper">
                    <slot name="empty-state"></slot>
                </div>
              `
            : mode === 'dots'
                ? this._renderDots(buckets, height)
                : this._renderBars(buckets, height);

        return html`
            <style>${sharedTokens}${panelStyles}</style>
            <div class="histogram-panel">
                ${title ? html`
                    <div class="histogram-header">
                        <p class="histogram-title">${title}</p>
                    </div>
                ` : nothing}
                <div class="histogram-body">
                    ${chart}
                </div>
            </div>
            <!-- Hidden slot for bucket/segment children -->
            <slot style="display:none"></slot>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportHistogram.tagName)) {
    customElements.define(PkReportHistogram.tagName, PkReportHistogram);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-histogram-bucket>` — a single time bucket within `<pk-report-histogram>`. v0.7
 *
 * Data-only element; no visual shadow DOM. Parent reads its attributes +
 * child `<pk-report-histogram-segment>` elements.
 *
 * Attributes:
 *   `bucket-start` — ISO instant of the bucket's left edge (required).
 *   `bucket-end`   — ISO instant of the bucket's right edge (required).
 */
export class PkReportHistogramBucket extends HTMLElement {
    static readonly tagName = 'pk-report-histogram-bucket';

    static get observedAttributes(): string[] {
        return ['bucket-start', 'bucket-end'];
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-histogram')?.dispatchEvent(
            new CustomEvent('_bucket-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportHistogramBucket.tagName)) {
    customElements.define(PkReportHistogramBucket.tagName, PkReportHistogramBucket);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-histogram-segment>` — a colored data segment within a bucket. v0.7
 *
 * Data-only element. Stacks within its parent bucket (bottom-to-top in bar
 * mode; contributes dots in dot density mode).
 *
 * Attributes:
 *   `category` — dimension label (e.g. "ai-usage").
 *   `count`    — number of events in this bucket for this category.
 *   `color`    — CSS color or var() reference.
 */
export class PkReportHistogramSegment extends HTMLElement {
    static readonly tagName = 'pk-report-histogram-segment';

    static get observedAttributes(): string[] {
        return ['category', 'count', 'color'];
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-histogram')?.dispatchEvent(
            new CustomEvent('_segment-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportHistogramSegment.tagName)) {
    customElements.define(PkReportHistogramSegment.tagName, PkReportHistogramSegment);
}

// ─────────────────────────────────────────────────────────────────────────────

// ── Compat data shapes (CMS-driven imperative API) ───────────────────────────

/** A single time/value point. Defensive key fallbacks tolerate CMS field naming. */
export interface PkHistogramPoint {
    t?:     string;
    time?:  string;
    x?:     string;
    date?:  string;
    v?:     number;
    value?: number;
    count?: number;
    y?:     number;
}

/** A named series for the multi-category `segments` setter. */
export interface PkHistogramSeries {
    label?:   string;
    color?:   string;
    buckets?: PkHistogramPoint[];
}

function histT(o: PkHistogramPoint): string {
    return o.t ?? o.time ?? o.x ?? o.date ?? '';
}

function histV(o: PkHistogramPoint): number {
    const v = o.v ?? o.value ?? o.count ?? o.y ?? 0;
    return typeof v === 'number' && isFinite(v) ? v : 0;
}

interface SegmentData {
    category: string;
    count:    number;
    color:    string;
}

interface BucketData {
    bucketStart: string;
    bucketEnd:   string;
    segments:    SegmentData[];
    totalCount:  number;
    breakdown:   Record<string, number>;
}

interface LayoutParams {
    n:          number;
    AXIS_HEIGHT: number;
    PADDING_H:  number;
    chartH:     number;
    totalW:     number;
    usableW:    number;
    bucketW:    number;
    BAR_GAP:    number;
    barW:       number;
    maxCount:   number;
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-histogram':         PkReportHistogram;
        'pk-report-histogram-bucket':  PkReportHistogramBucket;
        'pk-report-histogram-segment': PkReportHistogramSegment;
    }
}
